import { Body, Icon } from '@assembly-js/design-system';
import { Menu, MenuItem } from '@mui/material';
import { t } from 'i18next';
import React, { useState } from 'react';

interface CreditTierSelectProps {
  /** Selectable monthly credit totals (included allowance + ladder steps). */
  options: number[];
  /** The plan's included allowance, labeled "(included)" in the list. */
  includedCredits: number;
  /**
   * The subscription's current total, labeled "(current)" when it differs
   * from the included allowance.
   */
  currentCredits?: number;
  selectedCredits: number;
  onSelectCredits: (credits: number) => void;
}

export const CreditTierSelect: React.FC<CreditTierSelectProps> = ({
  options,
  includedCredits,
  currentCredits,
  selectedCredits,
  onSelectCredits,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const optionLabel = (credits: number): string => {
    if (credits === includedCredits) {
      return t(($) => $.page_studio_plans.choose_plan.credits_option_included, {
        count: credits,
      });
    }
    if (credits === currentCredits) {
      return t(($) => $.page_studio_plans.choose_plan.credits_option_current, {
        count: credits,
      });
    }
    return t(($) => $.page_studio_plans.choose_plan.credits_option, {
      count: credits,
    });
  };

  return (
    <>
      <button
        type="button"
        className="flex h-7 w-full cursor-pointer items-center justify-between rounded border border-solid border-divider-card bg-white px-2"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <Body size="sm" tag="span" className="text-black-heading">
          {optionLabel(selectedCredits)}
        </Body>
        <Icon icon="ChevronDown" className="h-3 w-3 text-black-heading" />
      </button>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ className: 'min-w-[200px]' }}
      >
        {options.map((credits) => (
          <MenuItem
            key={credits}
            selected={credits === selectedCredits}
            onClick={() => {
              onSelectCredits(credits);
              setAnchorEl(null);
            }}
          >
            <Body size="sm" tag="span">
              {optionLabel(credits)}
            </Body>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
